/**
 * Audit: the record of what people, their agents and the organisation's machines did. Every row
 * is a link in a hash chain, so the page can show that nothing was changed, and an auditor can
 * take the chain away and check it without trusting this service.
 *
 * The data column (Layout.tsx): the filters in the header (a search and two choices), the chain's
 * numbers, then two views. Entries is a table of compared columns (.data-table), the same thing done
 * again and again folded into one row (auditRows.ts); a row opens its detail, and the entries it
 * folds, in a sheet. The chain and forwarding holds the check, the export and where the trail and
 * the runs are sent.
 */

import { auditOriginWords, type AuditOrigin, type AuditVerifyResult } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { Clock, SealCheck, ShieldKeyhole } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { isCloud } from '../edition.ts';
import { actorKey, foldAudit, type AuditGroup, type AuditRow } from '../components/auditRows.ts';
import { auditSentence } from '../components/events.ts';
import { SubjectName } from '../components/SubjectPicker.tsx';
import { Button, ChoiceSelect, Empty, ErrorNote, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { FormSheet } from '../components/ui/FormSheet.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RunIdLabel } from '../components/ui/RunId.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { useNames } from '../names.ts';
import { listCount, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { holds, scopesHolding, usePermissions } from '../permissions.ts';
import { AuditExportButton, AuditForwardingSection } from './AuditForwardingPage.tsx';
import { RunForwardingSection } from '../components/RunForwardingSection.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

/** A ListBox id for the empty answer (none, any, all): an empty string is not a reliable id. */
const NONE = '__none';

/** The address and the user agent behind a row's quiet "from … · …" line, for its tooltip. */
const originTitle = (o: AuditOrigin | undefined): string | undefined => (o ? [o.ip ? `Address ${o.ip}` : undefined, o.userAgent].filter(Boolean).join('\n') || undefined : undefined);

type PeriodKey = 'day' | 'week' | 'month' | 'all';
const PERIODS: Record<PeriodKey, { label: string; since: (now: Date) => string | undefined }> = {
  day: { label: 'Last 24 hours', since: (n) => new Date(n.getTime() - 86_400_000).toISOString() },
  week: { label: 'Last 7 days', since: (n) => new Date(n.getTime() - 7 * 86_400_000).toISOString() },
  month: { label: 'Last 30 days', since: (n) => new Date(n.getTime() - 30 * 86_400_000).toISOString() },
  all: { label: 'All time', since: () => undefined },
};

type Machine = { name: string; kind: 'node' | 'workbench' };
type View = 'entries' | 'chain';

export function AuditPage() {
  const tone = VIEW_TONES.audit!;
  // A link may open the page on a search (`?q=`) and a period (`?period=day|week|month|all`).
  const [params, setParams] = useSearchParams();
  const search = useListSearch();
  const [q, setQ] = [search.typed, search.setTyped];
  useEffect(() => {
    const first = params.get('q');
    if (first) search.setTyped(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [period, setPeriod] = useState<PeriodKey>(() => {
    const p = params.get('period');
    return p && p in PERIODS ? (p as PeriodKey) : 'week';
  });
  // With roles on, a Team Administrator reads the rows that concern their teams (the API filters them); the chain, its
  // check and the export stay with whoever reads the organisation's trail. Until the answer is in, nothing asks for the chain.
  const perms = usePermissions();
  const orgReader: boolean | undefined = perms.loading && perms.data === undefined ? undefined : !perms.data || perms.data.mode !== 'roles' || holds(perms.data, 'audit.audit', 'read');
  const teamScopes = useMemo(() => (orgReader === false ? scopesHolding(perms.data, 'audit.audit', 'read', { type: 'team' }) : []), [orgReader, perms.data]);
  const [team, setTeam] = useState('');
  const view: View = params.get('view') === 'chain' && orgReader !== false ? 'chain' : 'entries';
  const setView = (v: View) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (v === 'chain') next.set('view', 'chain');
        else next.delete('view');
        return next;
      },
      { replace: true },
    );
  const since = useMemo(() => PERIODS[period].since(new Date()), [period]);
  const [action, setAction] = useState('');
  // Searched, filtered by team and paged on the server; the poll refreshes the newest page only, and Show older goes back from the last row.
  const query = { q: search.q || undefined, action: action || undefined, teamId: team || undefined, since };
  const rows = usePagedList((cursor) => routes.auditPage({ ...query, cursor }), query, { intervalMs: 30_000, keyOf: (r) => `${r.seq ?? r.ts}/${r.deviceId}/${r.action}/${r.ts}` });
  // The page checks quietly when it opens; a check a person asks for is recorded.
  const [asked, setAsked] = useState(0);
  const check = useLoad(() => (orgReader ? routes.auditVerify(asked > 0) : Promise.resolve(undefined)), [asked, orgReader]);
  const v: AuditVerifyResult | undefined = check.data;
  const list = rows.items;
  const teamName = (id: string) => teamScopes.find((t) => t.id === id)?.name ?? id;
  // Names for the ids the rows on screen carry: asked for those ids only, never every project and machine.
  const named = useNames(list.map((r) => r.projectId), list.map((r) => r.deviceId).filter((id) => id !== 'aioe'));
  const projectName = named.projects as Map<string, string>;
  const machine = useMemo(() => new Map<string, Machine>([...named.devices].map(([id, d]) => [id, { name: d.name, kind: d.kind === 'node' ? 'node' : 'workbench' }])), [named.devices]);
  const groups = useMemo(() => foldAudit(list), [list]);
  const [opened, setOpened] = useState<AuditGroup>();
  const keyOf = (g: AuditGroup) => `${g.head.seq ?? g.head.ts}`;
  const filtered = search.q !== '' || action !== '' || team !== '';

  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<SealCheck />}
        title="Audit"
        subtitle={orgReader === false ? `What concerned ${teamScopes.length === 1 ? `team ${teamName(teamScopes[0]!.id)}` : 'the teams you administer'}: its work, budgets, roles and reviews.` : 'What people, their agents and the organisation’s machines did, chained so a change to any row shows.'}
        actions={
          <>
            <SearchBox label="Search" value={q} onChange={setQ} placeholder="Search the trail" />
            {teamScopes.length > 1 ? <ChoiceSelect label="Team" className="w-40 max-w-full" value={team || NONE} options={{ [NONE]: 'All my teams', ...Object.fromEntries(teamScopes.map((t) => [t.id, teamName(t.id)])) }} onChange={(x) => setTeam(x === NONE ? '' : x)} /> : null}
            <ChoiceSelect label="What to show" className="w-40 max-w-full" value={action || NONE} options={{ [NONE]: 'Everything', 'tool.call': 'Tool calls', 'tool.review': 'Tool reviews' }} onChange={(x) => setAction(x === NONE ? '' : x)} />
            <ChoiceSelect label="Period" className="w-40 max-w-full" value={period} options={Object.fromEntries((Object.keys(PERIODS) as PeriodKey[]).map((k) => [k, PERIODS[k].label])) as Record<PeriodKey, string>} onChange={setPeriod} />
          </>
        }
      />
      <Explainer id="audit" tone={tone} icon={<SealCheck />} title="How the audit trail works" summary="Every row is a link in a chain, so a change to any of them shows">
        <p>Approvals, enrolments, dispatches, policy changes and what agents do on machines are recorded here as they happen. Each row’s hash covers the row and the hash before it, so changing or removing one breaks every link after it.</p>
        <p>The page checks the chain when it opens; Check the chain does it again and records that you asked. Keep the newest link somewhere else to prove later that nothing was cut off the end, or export the chain for an auditor to check offline.</p>
        <p>Search finds an action, a person, a target, a run id, an address or a place. The same thing done again and again is one row; open it to see each time.</p>
      </Explainer>
      <PartBoundary key="audit" name="Audit" tone={tone}>
        <ErrorNote error={rows.error ?? check.error} />
        {orgReader === false ? (
          <div className="art-stats">
            <Stat tone={tone} icon={<SealCheck />} label="Rows shown" value={rows.loaded ? list.length : undefined} detail={period === 'all' ? 'All time' : PERIODS[period].label} />
            <Stat tone={tone} icon={<Clock />} label="Your teams" value={teamScopes.length} detail={teamScopes.length ? teamScopes.map((t) => teamName(t.id)).slice(0, 3).join(', ') : 'None'} />
          </div>
        ) : (
        <div className="art-stats">
          <Stat tone={tone} icon={<SealCheck />} label="In the chain" value={v?.entries} detail={v ? (v.unchained ? `${v.unchained.toLocaleString()} older rows are from before the chain` : 'Every row is chained') : 'Checking'} />
          <Stat tone={tone} icon={<SealCheck />} label="Last check" value={v ? (v.ok ? 'Unchanged' : 'Broken') : undefined} detail={v ? (v.ok ? `Every link holds · ${timeAgo(v.verifiedAt)}` : `The link at ${v.broken?.seq} does not hold`) : ''} needsYou={v ? !v.ok : false} onPress={() => setView('chain')} />
          <Stat tone={tone} icon={<Clock />} label="Newest link" value={v?.head ? `#${v.head.seq.toLocaleString()}` : v ? '—' : undefined} detail={v?.head ? `${v.head.hash.slice(0, 16)}…` : 'Nothing recorded yet'} />
        </div>
        )}

        {orgReader === false ? null : (
          <Segment aria-label="Audit view" size="sm" selectedKey={view} onSelectionChange={(k) => setView(k === 'chain' ? 'chain' : 'entries')} className="w-fit max-w-full">
            <Segment.Item id="entries">Entries</Segment.Item>
            <Segment.Item id="chain">The chain and forwarding</Segment.Item>
          </Segment>
        )}

        {view === 'entries' ? (
          <ListGroup
            title="Entries"
            count={rows.loaded ? listCount(groups.length, { one: 'entry', many: 'entries' }, { hasMore: rows.hasMore, searched: filtered }) : undefined}
            description={`Newest first; open one for its detail.${groups.length < list.length ? ' The same thing done again and again is one row.' : ''}`}
            empty={
              rows.loaded && list.length === 0 ? (
                <Empty
                  tone={tone}
                  icon={<SealCheck />}
                  title={filtered ? 'Nothing matches' : 'Nothing recorded in this period'}
                  body={filtered ? 'Try another word, or a longer period.' : 'Approvals, enrolments, dispatches, policy changes and what agents do on machines are recorded here as they happen.'}
                  action={
                    period !== 'all' ? (
                      <Button size="sm" variant="secondary" onPress={() => setPeriod('all')}>
                        Look at all time
                      </Button>
                    ) : undefined
                  }
                />
              ) : undefined
            }
          >
            {!rows.loaded ? <ListBlock className="text-sm text-muted">Reading the trail…</ListBlock> : null}
            {groups.length ? (
              <ListBlock flush>
                <div className="data-table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="w-24 @[40rem]:w-28">When</th>
                        <th className="hidden w-48 @[40rem]:table-cell @[64rem]:w-56">Who</th>
                        <th>What</th>
                        <th className="hidden w-56 @[64rem]:table-cell">Where</th>
                        <th className="data-table__end hidden w-28 @[48rem]:table-cell">Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((g) => (
                        <EntryRow key={keyOf(g)} g={g} machine={machine} projectName={projectName} onOpen={() => setOpened(g)} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </ListBlock>
            ) : null}
            {rows.hasMore ? <ShowMore older onPress={rows.showMore} pending={rows.loadingMore} /> : null}
            {list.some((r) => r.origin?.location?.source === 'lookup') ? (
              <ListBlock className="text-xs text-muted">
                Places are approximate, worked out on this server from each address:{' '}
                <a href="https://db-ip.com" target="_blank" rel="noreferrer" className="text-accent hover:underline">
                  IP Geolocation by DB-IP
                </a>
                . What is kept, and for how long, is in{' '}
                <Link to="/settings/audit" className="text-accent hover:underline">
                  Settings
                </Link>
                .
              </ListBlock>
            ) : null}
          </ListGroup>
        ) : (
          <>
            <ListGroup title="The chain" description="Each row’s hash covers the row and the hash before it. Keep the newest link somewhere else to prove later that nothing was cut off the end.">
              <ListRow
                title={v ? (v.ok ? `All ${v.entries.toLocaleString()} links hold` : `The chain is broken at link ${v.broken?.seq}`) : 'Checking every link…'}
                description={v ? (v.ok ? `Checked ${timeAgo(v.verifiedAt)}.` : v.broken?.reason === 'gap' ? 'A row is missing.' : v.broken?.reason === 'link' ? 'It does not follow the row before it.' : 'The row was changed.') : undefined}
                status={v ? <StatusMark state={v.ok ? 'connected' : 'error'} label={v.ok ? 'Unchanged' : 'Broken'} /> : undefined}
                control={
                  <Button size="sm" variant="secondary" isPending={check.loading} onPress={() => setAsked((n) => n + 1)}>
                    Check the chain
                  </Button>
                }
              />
              {v?.head ? <ListRow title="Newest link" description={<span className="mono break-all">#{v.head.seq} · {v.head.hash}</span>} /> : null}
              <ListRow title="Export the chain" description="A period of the chain to download. For checking is one row per line with its hashes, then the newest link and a count: the form an auditor recomputes offline." control={<AuditExportButton />} />
              {isCloud() ? <ListRow to="/platform-access" icon={<ShieldKeyhole />} title="Support access" description="Each time our support team opened your organisation’s data, and what they looked at." /> : null}
            </ListGroup>
            <AuditForwardingSection tone={tone} />
            <RunForwardingSection tone={tone} />
          </>
        )}
      </PartBoundary>
      {opened ? <EntrySheet g={opened} machine={machine} projectName={projectName} onClose={() => setOpened(undefined)} /> : null}
    </PageColumn>
  );
}

/** Who did it, in words: the person, the platform, or the agent. */
function whoOf(a: AuditRow): ReactNode {
  const person = a.actor.principal ?? (a.actor.kind === 'user' ? a.actor.id : undefined);
  if (person) return <SubjectName id={person} fallback={person} />;
  if (a.actor.kind === 'system') return 'The platform';
  if (a.actor.kind === 'agent') return a.actor.agentName ?? a.actor.id ?? 'An agent';
  return a.actor.id ?? a.actor.kind;
}

/** Where it was done: a machine, or the console or the platform itself. */
function whereOf(a: AuditRow, machine: Map<string, Machine>): string {
  if (a.deviceId === 'aioe') return a.actor.kind === 'system' || a.actor.kind === 'schedule' ? 'The platform' : 'The console';
  const m = machine.get(a.deviceId);
  return m ? `${m.name} (${m.kind})` : a.deviceId;
}

const outcomeMark = (a: AuditRow) => (a.outcome === 'ok' ? null : <StatusMark state={a.outcome === 'denied' ? 'medium' : 'error'} label={a.outcome === 'denied' ? 'Refused' : 'Failed'} />);

function EntryRow({ g, machine, projectName, onOpen }: { g: AuditGroup; machine: Map<string, Machine>; projectName: Map<string, string>; onOpen: () => void }) {
  const a = g.head;
  const oldest = g.rows.at(-1)!;
  const m = a.deviceId === 'aioe' ? undefined : machine.get(a.deviceId);
  const who = whoOf(a);
  const origin = auditOriginWords(a.origin);
  return (
    <tr data-pressable onClick={onOpen}>
      <td className="text-muted" title={dateTime(a.ts)}>
        <span className="whitespace-nowrap">{timeAgo(a.ts)}</span>
        {g.rows.length > 1 && timeAgo(oldest.ts) !== timeAgo(a.ts) ? <span className="data-table__meta">to {timeAgo(oldest.ts)}</span> : null}
      </td>
      <td className="hidden @[40rem]:table-cell">
        <span className="line-clamp-2">{who}</span>
        {m ? <span className="data-table__meta truncate @[64rem]:hidden">on {m.name}</span> : null}
      </td>
      <td>
        {/* Narrow: the person goes above the sentence, so the sentence keeps the width. */}
        <span className="data-table__meta truncate @[40rem]:hidden">
          {who}
          {m ? ` · on ${m.name}` : ''}
        </span>
        <button type="button" className="data-table__open" onClick={(e) => (e.stopPropagation(), onOpen())}>
          {g.sentence ?? <span className="mono text-xs">{a.action}</span>}
        </button>
        {g.rows.length > 1 ? <span className="text-muted"> · {g.rows.length} times</span> : null}
        {a.outcome !== 'ok' ? <span className="mt-0.5 block">{outcomeMark(a)}</span> : null}
        {a.error ? <span className="data-table__meta truncate text-danger">{a.error}</span> : null}
        {origin ? (
          <span className="data-table__meta truncate @[64rem]:hidden" title={originTitle(a.origin)}>
            {origin}
          </span>
        ) : null}
      </td>
      <td className="hidden text-xs text-muted @[64rem]:table-cell">
        <span className="block truncate" title={a.deviceId}>
          {whereOf(a, machine)}
        </span>
        {origin ? (
          <span className="block line-clamp-2" title={originTitle(a.origin)}>
            {origin}
          </span>
        ) : null}
        {a.projectId ? (
          <Link to={`/projects/${encodeURIComponent(a.projectId)}`} className="line-clamp-2 hover:underline" title={a.projectId} onClick={(e) => e.stopPropagation()}>
            {projectName.get(a.projectId) ?? a.projectId}
          </Link>
        ) : null}
      </td>
      <td className="data-table__end hidden text-xs text-muted @[48rem]:table-cell" title={a.hash}>
        {a.seq !== undefined ? (g.rows.length > 1 && oldest.seq !== undefined ? `#${oldest.seq}–${a.seq}` : `#${a.seq}`) : 'before the chain'}
      </td>
    </tr>
  );
}

/** A label and its value in a detail sheet's two columns. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

/** One row's detail, and every entry it folds, in a sheet. */
function EntrySheet({ g, machine, projectName, onClose }: { g: AuditGroup; machine: Map<string, Machine>; projectName: Map<string, string>; onClose: () => void }) {
  const a = g.head;
  const single = g.rows.length === 1;
  const origin = auditOriginWords(a.origin);
  return (
    <FormSheet
      width="xl"
      title={g.sentence ?? a.action}
      description={`${dateTime(a.ts)}${single ? '' : ` · ${g.rows.length} entries, the newest first`}`}
      onClose={onClose}
      footer={
        <Button size="sm" variant="tertiary" onPress={onClose}>
          Close
        </Button>
      }
    >
      <dl className="grid gap-x-6 gap-y-2.5 text-sm sm:grid-cols-[9rem_minmax(0,1fr)]">
        <Fact label="Who">
          {whoOf(a)}
          {a.actor.kind === 'agent' && a.actor.agentName && a.actor.id && a.actor.id !== a.actor.agentName ? <span className="mono ms-2 text-xs text-muted">{a.actor.id}</span> : null}
        </Fact>
        <Fact label="Where">{whereOf(a, machine)}</Fact>
        {origin ? (
          <Fact label="From">
            {origin}
            {originTitle(a.origin) ? <span className="block whitespace-pre-line text-xs text-muted">{originTitle(a.origin)}</span> : null}
          </Fact>
        ) : null}
        {a.projectId ? (
          <Fact label="Project">
            <Link to={`/projects/${encodeURIComponent(a.projectId)}`} className="text-accent hover:underline" onClick={onClose}>
              {projectName.get(a.projectId) ?? a.projectId}
            </Link>
          </Fact>
        ) : null}
        <Fact label="Recorded as">
          <span className="mono text-xs">{a.action}</span>
          {a.target && a.target !== a.projectId ? <span className="text-muted"> · {a.target}</span> : null}
        </Fact>
        <Fact label="Outcome">{outcomeMark(a) ?? <StatusMark state="connected" label="Done" />}</Fact>
        {a.error ? <Fact label="Error">{a.error}</Fact> : null}
        {a.runId ? (
          <Fact label="Run">
            <RunIdLabel runId={a.runId} />
          </Fact>
        ) : null}
        <Fact label="Link">
          {a.seq !== undefined ? (
            <>
              <span className="tabular-nums">#{a.seq}</span>
              {a.hash ? <span className="mono block break-all text-xs text-muted">{a.hash}</span> : null}
            </>
          ) : (
            'Before the chain'
          )}
        </Fact>
        {single && a.detail ? (
          <Fact label="Detail">
            <pre className="mono overflow-x-auto whitespace-pre-wrap rounded-lg bg-surface-secondary p-2.5 text-xs">{JSON.stringify(a.detail, null, 2)}</pre>
          </Fact>
        ) : null}
      </dl>
      {!single ? (
        <ListGroup title="Each time" count={g.rows.length}>
          {g.rows.map((r, i) => (
            <ListRow
              key={r.seq ?? i}
              title={auditSentence(r) ?? <span className="mono text-xs">{r.action}</span>}
              description={
                <>
                  <span className="block" title={dateTime(r.ts)}>
                    {timeAgo(r.ts)}
                    {actorKey(r) !== actorKey(a) ? ` · ${actorKey(r)}` : ''}
                    {auditOriginWords(r.origin) ? ` · ${auditOriginWords(r.origin)}` : ''}
                  </span>
                  {r.error ? <span className="block text-danger">{r.error}</span> : null}
                </>
              }
              value={r.seq !== undefined ? <span className="text-xs tabular-nums" title={r.hash}>#{r.seq}</span> : undefined}
            />
          ))}
        </ListGroup>
      ) : null}
    </FormSheet>
  );
}
